/**
 * Thin fetch wrapper.
 *
 * `credentials: "same-origin"` on everything, because the session is a cookie and there is no token
 * to attach. A 403 is turned into a typed error the UI can recognise, since the backend answers
 * "not logged in", "wrong tier" and "no such route" identically on purpose — deny by default,
 * leaking nothing about what exists — so the client cannot and should not try to tell them apart.
 */

import type {
  AltsResponse,
  AuditEntry,
  DiscordEvent,
  DiscordSummary,
  LogMeta,
  LogPage,
  DupeIncident,
  Evidence,
  IssuedPassword,
  LoaEntry,
  PortalPunishment,
  Me,
  StaffAccount,
  Tier,
  Overview,
  PlayerSummary,
  Punishment,
  RefundRecord,
  SpawnerStash,
  VoiceEvent,
  WagerDebugRow,
  WagerFight,
} from "./types";

export class NotAuthorizedError extends Error {
  constructor() {
    super("not authorized");
    this.name = "NotAuthorizedError";
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { credentials: "same-origin", signal });
  if (response.status === 403) throw new NotAuthorizedError();
  if (!response.ok) throw new Error(`${path} failed: HTTP ${response.status}`);
  return (await response.json()) as T;
}

export function fetchMe(): Promise<Me> {
  return get<Me>("/api/me");
}

export interface PlayerCounts {
  total: number;
  updatedAt: number;
  proxies: Array<{ id: string; label: string; players: number }>;
}

/** Public: no session needed, so this works on the sign-in screen. */
export function fetchPlayerCounts(): Promise<PlayerCounts> {
  return get<PlayerCounts>("/api/players");
}

export interface OnlineRoster {
  total: number;
  updatedAt: number;
  proxies: Array<{ id: string; label: string; players: Array<{ uuid: string; name: string }> }>;
}

/** Signed-in only: who is on each proxy, by name. */
export function fetchOnlineRoster(): Promise<OnlineRoster> {
  return get<OnlineRoster>("/api/players/online");
}

export function fetchOverview(): Promise<Overview> {
  return get<Overview>("/api/overview");
}

export function fetchDupeIncidents(
  query: string,
  cursor: string | null,
): Promise<{ incidents: DupeIncident[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/dupe?${params.toString()}`);
}

/**
 * Queues a refund for one voided item.
 *
 * Sends only the log row id — the server reads the item, the recipient and the stamp out of that row
 * itself. Nothing about the item travels from the browser, deliberately.
 */
export async function issueDupeRefund(
  logId: number,
  csrfToken: string,
): Promise<{ refund: RefundRecord }> {
  const response = await fetch(`/api/dupe/${logId}/refund`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrfToken },
  });
  if (response.status === 403) throw new NotAuthorizedError();
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string; detail?: string };
    throw new Error(body.detail ?? body.error ?? `Refund failed (HTTP ${response.status})`);
  }
  return (await response.json()) as { refund: RefundRecord };
}

export function fetchDupeRefund(refundId: number): Promise<{ refund: RefundRecord }> {
  return get(`/api/dupe/refund/${refundId}`);
}

export function fetchSpawnerStashes(
  cursor: string | null,
): Promise<{ stashes: SpawnerStash[]; nextCursor: string | null }> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  return get(`/api/spawner-stashes?${params.toString()}`);
}

export function fetchSpawnerStash(id: number): Promise<{ stash: SpawnerStash }> {
  return get(`/api/spawner-stashes/${id}`);
}

// ---- wager debug (admin only; the server refuses everyone else) -----------------------------------

export function fetchWagerFights(
  query: string,
  cursor: string | null,
): Promise<{ fights: WagerFight[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/wager-debug/fights?${params.toString()}`);
}

export function fetchWagerFight(
  stamp: string,
): Promise<{ stamp: string; wagerId: string | null; rows: WagerDebugRow[]; truncated: boolean }> {
  return get(`/api/wager-debug/fights/${encodeURIComponent(stamp)}`);
}

export function fetchWagerRows(
  query: string,
  cursor: string | null,
): Promise<{ rows: WagerDebugRow[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/wager-debug/rows?${params.toString()}`);
}

export function fetchLogMeta(): Promise<LogMeta> {
  return get<LogMeta>("/api/logs/meta");
}

export function fetchLogs(query: string, cursor: string | null, signal?: AbortSignal): Promise<LogPage> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get<LogPage>(`/api/logs?${params.toString()}`, signal);
}

export function searchPlayers(term: string, signal?: AbortSignal): Promise<{ players: string[] }> {
  return get<{ players: string[] }>(`/api/players/search?q=${encodeURIComponent(term)}`, signal);
}

export function fetchPlayerSummary(id: string): Promise<PlayerSummary> {
  return get<PlayerSummary>(`/api/players/${encodeURIComponent(id)}/summary`);
}

export function fetchPunishments(
  id: string,
): Promise<{ punishments: Punishment[]; kickHistoryAvailable: boolean }> {
  return get(`/api/players/${encodeURIComponent(id)}/punishments`);
}

/** Deliberately its own call rather than folded into the summary: fetching alts writes an audit row,
 *  so it must happen when somebody actually asks to see them, not on every drawer open. */
export function fetchAlts(id: string): Promise<AltsResponse> {
  return get<AltsResponse>(`/api/players/${encodeURIComponent(id)}/alts`);
}

export function fetchVoiceEvents(
  query: string,
  cursor: string | null,
): Promise<{ events: VoiceEvent[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/voice?${params.toString()}`);
}

export function fetchDiscordEvents(
  query: string,
  cursor: string | null,
): Promise<{ events: DiscordEvent[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/discord?${params.toString()}`);
}

export function fetchDiscordSummary(): Promise<DiscordSummary> {
  return get<DiscordSummary>("/api/discord/summary");
}

export function fetchVoiceRules(): Promise<{ rules: string[] }> {
  return get<{ rules: string[] }>("/api/voice/rules");
}

export function fetchAudit(
  query: string,
  cursor: string | null,
): Promise<{ entries: AuditEntry[]; nextCursor: string | null }> {
  const params = new URLSearchParams(query);
  if (cursor) params.set("cursor", cursor);
  return get(`/api/admin/audit?${params.toString()}`);
}

export interface ShareLink {
  url: string;
  token: string;
  expiresAt: number;
}

/**
 * Mints an expiring public link for one clip.
 *
 * POST with the CSRF token, because this creates something that hands a recording to somebody
 * outside the staff system — exactly the kind of action that must not be triggerable by a page a
 * signed-in staff member merely happens to visit.
 */
export async function createShareLink(
  eventId: number,
  ttlSeconds: number,
  note: string,
  csrfToken: string,
): Promise<ShareLink> {
  const response = await fetch(`/api/voice/${eventId}/share`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
    body: JSON.stringify({ ttlSeconds, note }),
  });
  if (response.status === 403) throw new NotAuthorizedError();
  if (!response.ok) throw new Error(`Could not create a share link (HTTP ${response.status})`);
  return (await response.json()) as ShareLink;
}

export async function revokeShareLink(token: string, csrfToken: string): Promise<void> {
  await fetch(`/api/voice/share/${token}`, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrfToken },
  });
}

export function fetchShareOptions(): Promise<{
  options: Array<{ label: string; seconds: number }>;
  defaultSeconds: number;
}> {
  return get("/api/voice/share-options");
}

/** POST with a JSON body; a non-2xx turns into an Error carrying the server's message. */
async function send<T>(
  method: string,
  path: string,
  body: unknown,
  csrfToken: string | null,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = (await response.json().catch(() => ({}))) as { error?: string };
  if (!response.ok) {
    throw new Error(json.error && json.error !== "not authorized" ? json.error : `Request failed (HTTP ${response.status})`);
  }
  return json as T;
}

export function login(
  username: string,
  password: string,
): Promise<{ ok: true; mustChangePassword: boolean }> {
  return send("POST", "/auth/login", { username, password }, null);
}

export function changePassword(
  currentPassword: string,
  newPassword: string,
  csrfToken: string,
): Promise<{ ok: true }> {
  return send("POST", "/auth/password", { currentPassword, newPassword }, csrfToken);
}

export function fetchAccounts(): Promise<{ canManage: boolean; users: StaffAccount[] }> {
  return get("/api/admin/users");
}

export function createAccount(username: string, tier: Tier, csrfToken: string): Promise<IssuedPassword> {
  return send("POST", "/api/admin/users", { username, tier }, csrfToken);
}

export function resetAccountPassword(id: string, csrfToken: string): Promise<IssuedPassword> {
  return send("POST", `/api/admin/users/${encodeURIComponent(id)}/reset-password`, undefined, csrfToken);
}

export function changeAccountTier(id: string, tier: Tier, csrfToken: string): Promise<{ ok: true }> {
  return send("POST", `/api/admin/users/${encodeURIComponent(id)}/tier`, { tier }, csrfToken);
}

/** Admin: link (or, with empty values, unlink) the Minecraft account a staff account acts as. */
export function setAccountMinecraft(
  id: string,
  uuid: string,
  name: string,
  csrfToken: string,
): Promise<{ ok: true; minecraft: { uuid: string; name: string } | null }> {
  return send("PUT", `/api/admin/users/${encodeURIComponent(id)}/minecraft`, { uuid, name }, csrfToken);
}

export function deleteAccount(id: string, csrfToken: string): Promise<{ ok: true }> {
  return send("DELETE", `/api/admin/users/${encodeURIComponent(id)}`, undefined, csrfToken);
}

// ---- staff portal -------------------------------------------------------------------------------

export function fetchLoa(): Promise<{ today: string; entries: LoaEntry[] }> {
  return get("/api/portal/loa");
}

export function createLoa(
  startsOn: string,
  endsOn: string,
  reason: string,
  csrfToken: string,
  /** Admins only: the account to put on LOA. Omit to log your own. */
  forUserId?: string,
): Promise<{ id: number }> {
  return send("POST", "/api/portal/loa", { startsOn, endsOn, reason, forUserId }, csrfToken);
}

export function endLoa(id: number, csrfToken: string): Promise<{ ok: true }> {
  return send("POST", `/api/portal/loa/${id}/end`, undefined, csrfToken);
}

export function deleteLoa(id: number, csrfToken: string): Promise<{ ok: true }> {
  return send("DELETE", `/api/portal/loa/${id}`, undefined, csrfToken);
}

export function portalSearchPlayers(term: string, signal?: AbortSignal): Promise<{ players: string[] }> {
  return get(`/api/portal/players/search?q=${encodeURIComponent(term)}`, signal);
}

export function fetchPortalPunishments(player: string): Promise<{
  player: string | null;
  uuid: string | null;
  punishments: PortalPunishment[];
  kickHistoryAvailable: boolean;
}> {
  return get(`/api/portal/players/${encodeURIComponent(player)}/punishments`);
}

export interface MissingEvidence {
  kind: "ban" | "mute" | "kick";
  id: string;
  player: string | null;
  reason: string;
  issuer: string;
  issued_at: number;
}

export function fetchMissingEvidence(
  days: number,
  issuer: string,
): Promise<{ days: number; punishments: MissingEvidence[] }> {
  const params = new URLSearchParams({ days: String(days) });
  if (issuer.trim()) params.set("issuer", issuer.trim());
  return get(`/api/portal/missing-evidence?${params.toString()}`);
}

export function fetchEvidence(kind: string, id: string): Promise<{ evidence: Evidence[] }> {
  return get(`/api/portal/punishments/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/evidence`);
}

/** Sends the file as the raw request body, typed by its own MIME type. */
export async function uploadEvidence(
  kind: string,
  id: string,
  file: File,
  note: string,
  csrfToken: string,
): Promise<{ id: number }> {
  const params = new URLSearchParams({ name: file.name });
  if (note.trim()) params.set("note", note.trim());
  const response = await fetch(
    `/api/portal/punishments/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/evidence?${params.toString()}`,
    {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": file.type || "application/octet-stream", "X-CSRF-Token": csrfToken },
      body: file,
    },
  );
  const json = (await response.json().catch(() => ({}))) as { error?: string; message?: string; id?: number };
  if (response.status === 413) throw new Error("That file is too big to upload.");
  if (!response.ok) throw new Error(json.error && json.error !== "not authorized" ? json.error : `Upload failed (HTTP ${response.status})`);
  return json as { id: number };
}

/** True for a medal.tv link, which is imported with {@link importMedalEvidence} rather than uploaded. */
export function isMedalLink(text: string): boolean {
  try {
    const url = new URL(text.trim());
    const host = url.hostname.toLowerCase();
    return url.protocol === "https:" && (host === "medal.tv" || host.endsWith(".medal.tv"));
  } catch {
    return false;
  }
}

/** The server downloads the Medal clip and stores it as an ordinary evidence file. */
export function importMedalEvidence(
  kind: string,
  id: string,
  url: string,
  note: string,
  csrfToken: string,
): Promise<{ id: number }> {
  return send(
    "POST",
    `/api/portal/punishments/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/evidence/medal`,
    { url: url.trim(), note: note.trim() },
    csrfToken,
  );
}

/** Same-origin, cookie-authorised URL for showing evidence inside the dashboard. Not shareable: it
 *  only works for a signed-in staff member. */
export function evidenceFileUrl(id: number, download = false): string {
  return `/api/portal/evidence/${id}/file${download ? "?download=1" : ""}`;
}

export function deleteEvidence(id: number, csrfToken: string): Promise<{ ok: true }> {
  return send("DELETE", `/api/portal/evidence/${id}`, undefined, csrfToken);
}

// ---- desktop app download -----------------------------------------------------------------------

export interface AppInfo {
  available: boolean;
  fileName?: string;
  bytes?: number;
  version?: string | null;
  uploadedAt?: number;
  uploadedBy?: string;
  /** True when uploaded with its update.json, so the app's auto-updater will install it. */
  signed?: boolean;
  sha256?: string | null;
}

export function fetchAppInfo(): Promise<AppInfo> {
  return get<AppInfo>("/api/app");
}

export const APP_DOWNLOAD_URL = "/api/app/download";

/**
 * Uploads a new app file, reporting progress (0-1). XMLHttpRequest rather than fetch, because fetch
 * cannot report upload progress and an installer can take a minute to send.
 */
export function uploadApp(
  file: File,
  /** The release's update.json, as text. */
  manifest: string,
  csrfToken: string,
  onProgress: (fraction: number) => void,
): Promise<AppInfo> {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams({ name: file.name, manifest });
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/app?${params.toString()}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("Content-Type", "application/x-prism-app");
    xhr.setRequestHeader("X-CSRF-Token", csrfToken);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      let body: { error?: string } & AppInfo = { available: false };
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        // Not JSON — nginx's own error page, most likely.
      }
      if (xhr.status === 413) return reject(new Error("That file is too big for the server's upload limit."));
      if (xhr.status < 200 || xhr.status >= 300) {
        return reject(new Error(body.error && body.error !== "not authorized" ? body.error : `Upload failed (HTTP ${xhr.status})`));
      }
      resolve(body);
    };
    xhr.onerror = () => reject(new Error("The upload was interrupted. Check your connection and try again."));
    xhr.send(file);
  });
}

export function removeApp(csrfToken: string): Promise<AppInfo> {
  return send("DELETE", "/api/app", undefined, csrfToken);
}

// ---- account security ---------------------------------------------------------------------------

export interface LoginAlert {
  id: number;
  device: string;
  location: string;
  time: number;
  status?: "open" | "confirmed" | "rejected";
  resolvedAt?: number | null;
}

export interface SecurityInfo {
  sessions: Array<{ handle: string; current: boolean; device: string; ip: string | null; createdAt: number | null }>;
  devices: Array<{
    id: number;
    description: string;
    firstSeen: number;
    lastSeen: number;
    ip: string | null;
    trusted: boolean;
    current: boolean;
  }>;
  alerts: LoginAlert[];
}

export function fetchSecurity(): Promise<SecurityInfo> {
  return get("/api/account/security");
}

export function confirmLoginAlert(id: number, csrfToken: string): Promise<{ ok: true }> {
  return send("POST", `/api/account/alerts/${id}/confirm`, undefined, csrfToken);
}

export function rejectLoginAlert(
  id: number,
  csrfToken: string,
): Promise<{ ok: true; sessionsEnded: number; passwordResetRequired: boolean }> {
  return send("POST", `/api/account/alerts/${id}/reject`, undefined, csrfToken);
}

export function signOutSession(handle: string, csrfToken: string): Promise<{ ok: true; signedOutSelf: boolean }> {
  return send("POST", `/api/account/sessions/${encodeURIComponent(handle)}/signout`, undefined, csrfToken);
}

export function signOutEverywhere(csrfToken: string): Promise<{ ok: true; sessionsEnded: number }> {
  return send("POST", "/api/account/sessions/signout-all", undefined, csrfToken);
}

export async function logout(csrfToken: string): Promise<void> {
  await fetch("/auth/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-Token": csrfToken },
  });
}
